import React, { useState, useEffect } from 'react';
import { getAllDealerUsers, setDealerUser, deleteDealerUser } from '../services/api';
import AssistantDropdown from './AssistantDropdown';
import TalkliveInstancesAccessSection from './TalkliveInstancesAccessSection';
import './DealerUsers.css';

// Modal component for editing dealer user
function EditUserModal({ isOpen, user, onClose, onSave, onDelete }) {
  const [formData, setFormData] = useState({
    firebase_uid: '',
    email: '',
    first_name: '',
    last_name: '',
    assistants_access: [],
    talklive_instances: [],
    role: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        firebase_uid: user.firebase_uid || '',
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        assistants_access: user.assistants_access || [],
        talklive_instances: user.talklive_instances || [],
        role: user.role || ''
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssistantsChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      assistants_access: prev.assistants_access.map((assistant, i) => 
        i === index ? value : assistant
      )
    }));
  };

  const addAssistant = () => {
    setFormData(prev => ({
      ...prev,
      assistants_access: [...prev.assistants_access, '']
    }));
  };

  const removeAssistant = (index) => {
    setFormData(prev => ({
      ...prev,
      assistants_access: prev.assistants_access.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = { ...formData };
      // Send role as null if empty (full access)
      if (!submitData.role) {
        submitData.role = null;
      }
      await setDealerUser(submitData);
      onSave();
      onClose();
    } catch (err) {
      setError('Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    setError('');

    try {
      await deleteDealerUser(formData.firebase_uid);
      onDelete();
      onClose();
    } catch (err) {
      setError('Failed to delete user');
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Edit Dealer User</h2>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Firebase UID:</label>
            <input
              type="text"
              value={formData.firebase_uid}
              disabled
              className="disabled-input"
            />
          </div>
          <div className="form-group">
            <label>Email:</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label>First Name:</label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Last Name:</label>
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Assistants Access:</label>
            <div className="assistants-container">
              {formData.assistants_access.map((assistant, index) => (
                <div key={index} className="assistant-input-row">
                  <AssistantDropdown
                    value={assistant}
                    onChange={(value) => handleAssistantsChange(index, value)}
                    placeholder="Select assistant..."
                  />
                  <button
                    type="button"
                    onClick={() => removeAssistant(index)}
                    className="remove-assistant-btn"
                  >
                    -
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addAssistant}
                className="add-assistant-btn"
              >
                + Add Assistant
              </button>
            </div>
          </div>
          <div className="form-group">
            <label>Role:</label>
            <select
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              className="role-select"
            >
              <option value="">Full Access</option>
              <option value="missed_calls_only">Missed Calls Only</option>
              <option value="conversations_leads_only">Conversations and Leads Only</option>
            </select>
          </div>
          <TalkliveInstancesAccessSection
            isOpen={isOpen}
            assistantsAccess={formData.assistants_access}
            instances={formData.talklive_instances}
            onInstancesChange={(talklive_instances) =>
              setFormData((prev) => ({ ...prev, talklive_instances }))
            }
          />
          <div className="modal-buttons">
            <button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </button>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="delete-btn"
              disabled={loading}
            >
              Delete
            </button>
          </div>
        </form>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content delete-confirm-modal">
            <h2>Confirm Delete</h2>
            <p>Are you sure you want to delete the user "{formData.email}"?</p>
            <p>This action cannot be undone.</p>
            <div className="modal-buttons">
              <button 
                type="button" 
                onClick={handleDelete}
                disabled={deleteLoading}
                className="confirm-delete-btn"
              >
                {deleteLoading ? 'Deleting...' : 'Delete User'}
              </button>
              <button 
                type="button" 
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Modal component for creating new dealer user
function CreateUserModal({ isOpen, onClose, onSave }) {
  const [formData, setFormData] = useState({
    firebase_uid: '',
    email: '',
    first_name: '',
    last_name: '',
    assistants_access: [],
    talklive_instances: [],
    role: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssistantsChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      assistants_access: prev.assistants_access.map((assistant, i) => 
        i === index ? value : assistant
      )
    }));
  };

  const addAssistant = () => {
    setFormData(prev => ({
      ...prev,
      assistants_access: [...prev.assistants_access, '']
    }));
  };

  const removeAssistant = (index) => {
    setFormData(prev => ({
      ...prev,
      assistants_access: prev.assistants_access.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Send empty string for firebase_uid to let backend create new user
      const createData = {
        ...formData,
        firebase_uid: ''
      };
      // Send role as null if empty (full access)
      if (!createData.role) {
        createData.role = null;
      }
      await setDealerUser(createData);
      onSave();
      onClose();
      // Reset form
      setFormData({
        firebase_uid: '',
        email: '',
        first_name: '',
        last_name: '',
        assistants_access: [],
        talklive_instances: [],
        role: ''
      });
    } catch (err) {
      setError('Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Create New Dealer User</h2>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email:</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label>First Name:</label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Last Name:</label>
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Assistants Access:</label>
            <div className="assistants-container">
              {formData.assistants_access.map((assistant, index) => (
                <div key={index} className="assistant-input-row">
                  <AssistantDropdown
                    value={assistant}
                    onChange={(value) => handleAssistantsChange(index, value)}
                    placeholder="Select assistant..."
                  />
                  <button
                    type="button"
                    onClick={() => removeAssistant(index)}
                    className="remove-assistant-btn"
                  >
                    -
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addAssistant}
                className="add-assistant-btn"
              >
                + Add Assistant
              </button>
            </div>
          </div>
          <div className="form-group">
            <label>Role:</label>
            <select
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              className="role-select"
            >
              <option value="">Full Access</option>
              <option value="missed_calls_only">Missed Calls Only</option>
              <option value="conversations_leads_only">Conversations and Leads Only</option>
            </select>
          </div>
          <TalkliveInstancesAccessSection
            isOpen={isOpen}
            assistantsAccess={formData.assistants_access}
            instances={formData.talklive_instances}
            onInstancesChange={(talklive_instances) =>
              setFormData((prev) => ({ ...prev, talklive_instances }))
            }
          />
          <div className="modal-buttons">
            <button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create'}
            </button>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const DealerUsers = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Fetch all dealer users on mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users based on search term
  useEffect(() => {
    const filtered = users.filter(user => {
      // If no search term, show all users
      if (!searchTerm) {
        return true;
      }
      // If user has email, check if it matches search
      if (user.email) {
        return user.email.toLowerCase().includes(searchTerm.toLowerCase());
      }
      // If no email, don't show in search results
      return false;
    });
    setFilteredUsers(filtered);
  }, [users, searchTerm]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getAllDealerUsers();
      const usersData = response.data || [];
      // Ensure all users have required fields
      const validatedUsers = usersData.map(user => ({
        firebase_uid: user.firebase_uid || '',
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        assistants_access: user.assistants_access || [],
        talklive_instances: user.talklive_instances || [],
        role: user.role || ''
      }));
      setUsers(validatedUsers);
    } catch (err) {
      setError('Failed to load dealer users');
      console.error('Error fetching dealer users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleSaveUser = () => {
    fetchUsers(); // Refresh the list after saving
  };

  const handleDeleteUser = () => {
    fetchUsers(); // Refresh the list after deleting
  };

  if (loading) {
    return <div className="loading">Loading dealer users...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="dealer-users">
      <h1>Dealer Users</h1>
      
      <div className="search-container">
        <div className="search-row">
          <input
            type="text"
            placeholder="Search by email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button
            onClick={() => setShowCreateModal(true)}
            className="create-user-btn"
          >
            + Create New User
          </button>
        </div>
      </div>

      <div className="users-grid">
        {filteredUsers.map((user, index) => (
          <button
            key={user.firebase_uid || index}
            className="user-button"
            onClick={() => handleUserClick(user)}
          >
            {user.email || 'No email'}
          </button>
        ))}
      </div>

      {filteredUsers.length === 0 && !loading && (
        <div className="no-users">
          {searchTerm ? 'No users found matching your search.' : 'No dealer users found.'}
        </div>
      )}

      <EditUserModal
        isOpen={showEditModal}
        user={selectedUser}
        onClose={() => {
          setShowEditModal(false);
          setSelectedUser(null);
        }}
        onSave={handleSaveUser}
        onDelete={handleDeleteUser}
      />

      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleSaveUser}
      />
    </div>
  );
};

export default DealerUsers; 